import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"

const log = logger("DeepDiveEventBus")

export enum DeepDiveMode {
  ALL_METRICS = "ALL_METRICS",
  SELECTED_METRIC = "SELECTED_METRIC",
}

export const EVENTS = {
  MODE_CHANGE: "MODE_CHANGE",
  AUDIO_PLAY: "AUDIO_PLAY",
  AUDIO_PAUSE: "AUDIO_PAUSE",
  AUDIO_TOGGLE: "AUDIO_TOGGLE",
  AUDIO_SEEK: "AUDIO_SEEK",
  AUDIO_TIME: "AUDIO_TIME",
  AUDIO_REGIONS_HOVER: "AUDIO_REGIONS_HOVER",
  AUDIO_HOVER_TIME: "AUDIO_HOVER_TIME",
  TRACE_HOVER_TIME: "TRACE_HOVER_TIME",
  MESSAGE_SELECTED: "MESSAGE_SELECTED",
  MESSAGES_HOVER: "MESSAGES_HOVER",
  TEST_DETAILS_CHANGE: "TEST_DETAILS_CHANGE",
} as const

export type EventType = (typeof EVENTS)[keyof typeof EVENTS]

export interface ModeChangePayload {
  type: DeepDiveMode
  data: string
}

export interface AudioPlayPayload {
  time?: number
}

export interface AudioPausePayload {
  time?: number
}

export type AudioTogglePayload = Record<string, never>

export interface AudioSeekPayload {
  time: number
  /**
   * The result this seek is meant for. Navigation can leave a surface asking
   * for a position while the player has already moved on, and moving the
   * wrong recording is worse than ignoring the request.
   */
  simulationOutputId?: string
}

// Broadcast playback position so deep-dive surfaces (plots, transcript) can track it.
export interface AudioTimePayload {
  time: number
  /** Original requested position for an applied seek, before the media clamps it. */
  seekTarget?: number
  /**
   * The result this position belongs to. A late subscriber reading the last
   * payload has no other way to tell a live position from one left behind by
   * the result it was viewing before.
   */
  simulationOutputId?: string
}

/**
 * Where a set of hovered regions came from, which decides both what it
 * outranks and who is allowed to end it.
 *
 * `"card"` is the whole-metric set a card states by being passed over, which
 * the regions an open card has pinned outrank. `"detail"` is a moment pointed
 * at inside a card — a criterion's referenced message — and shows over the
 * pinned set; it lives and dies with the card holding it, so opening another
 * metric ends it. `"span"` is a trace span marked from the traces view, which
 * also shows over the pinned set but belongs to no metric: only the viewer
 * that sent it takes it away.
 */
export type AudioRegionsHoverSource = "card" | "detail" | "span"

export interface AudioRegionsHoverPayload {
  regions: AudioRegionParams[] | null
  /** Defaults to `"card"`. */
  source?: AudioRegionsHoverSource
}

/**
 * The moment on the recording the pointer is over, so a surface on the same
 * clock can mark it too. `AUDIO_HOVER_TIME` carries a hover on the waveform and
 * `TRACE_HOVER_TIME` one on the trace timeline; each surface draws only the
 * other's, since it already draws its own. Kept as two events so the last
 * payload of each still says where that surface's pointer is, whatever the
 * other one sent since.
 */
export interface AudioHoverTimePayload {
  /** Seconds into the recording, or null once the pointer leaves. */
  time: number | null
  /** The result the hover was over, so a surface on another one ignores it. */
  simulationOutputId?: string
}

export interface MessageSelectedPayload {
  messageIndex: number | null
}

export interface MessagesHoverPayload {
  messageIndices: number[] | null
  metricId?: string
}

export type TestDetailsSection =
  | "persona"
  | "testCase"
  | "expectedOutput"
  | "simulationMetadata"
  | "customerMetadata"
  | null

export interface TestDetailsChangePayload {
  showTestDetails: boolean
  activeSection: TestDetailsSection
}

export interface EventPayloads {
  [EVENTS.MODE_CHANGE]: ModeChangePayload
  [EVENTS.AUDIO_PLAY]: AudioPlayPayload
  [EVENTS.AUDIO_PAUSE]: AudioPausePayload
  [EVENTS.AUDIO_TOGGLE]: AudioTogglePayload
  [EVENTS.AUDIO_SEEK]: AudioSeekPayload
  [EVENTS.AUDIO_TIME]: AudioTimePayload
  [EVENTS.AUDIO_REGIONS_HOVER]: AudioRegionsHoverPayload
  [EVENTS.AUDIO_HOVER_TIME]: AudioHoverTimePayload
  [EVENTS.TRACE_HOVER_TIME]: AudioHoverTimePayload
  [EVENTS.MESSAGE_SELECTED]: MessageSelectedPayload
  [EVENTS.MESSAGES_HOVER]: MessagesHoverPayload
  [EVENTS.TEST_DETAILS_CHANGE]: TestDetailsChangePayload
}

export type EventListener<T extends EventType> = (
  payload: EventPayloads[T],
) => void

class EventBus {
  private listeners: Map<EventType, Set<EventListener<EventType>>> = new Map()
  private lastPayloads: Map<EventType, unknown> = new Map()

  subscribe<T extends EventType>(
    eventType: T,
    listener: EventListener<T>,
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }

    this.listeners.get(eventType)!.add(listener as EventListener<EventType>)

    return () => {
      const eventListeners = this.listeners.get(eventType) as
        | Set<EventListener<T>>
        | undefined
      if (eventListeners) {
        eventListeners.delete(listener)
        if (eventListeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  /**
   * The most recent payload for an event, or undefined if none has been sent.
   *
   * A listener only hears what is dispatched after it subscribes, so a surface
   * that mounts partway through — a tab opened mid-playback — has no way to
   * learn the current state from the bus alone. Reading the last payload lets
   * it start from where everything else already is.
   */
  getLastPayload<T extends EventType>(
    eventType: T,
  ): EventPayloads[T] | undefined {
    return this.lastPayloads.get(eventType) as EventPayloads[T] | undefined
  }

  dispatch<T extends EventType>(eventType: T, payload: EventPayloads[T]): void {
    this.lastPayloads.set(eventType, payload)
    const eventListeners = this.listeners.get(eventType) as
      | Set<EventListener<T>>
      | undefined
    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(payload)
        } catch (error) {
          log.error(`Error in event listener for ${eventType}`, error)
        }
      })
    }
  }

  removeAllListeners(eventType: EventType): void {
    this.listeners.delete(eventType)
    this.lastPayloads.delete(eventType)
  }

  clear(): void {
    this.listeners.clear()
    this.lastPayloads.clear()
  }
}

export const simulationDeepDiveEventBus = new EventBus()
